import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  Skeleton,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import trainingService from '@/services/trainingService';
import {
  TrainingGuide,
  TrainingProgress,
  TrainingFilters,
  TrainingCategory,
  DifficultyLevel
} from '@/types/training';

const CATEGORIES: { value: TrainingCategory; label: string }[] = [
  { value: 'getting-started', label: 'Getting Started' },
  { value: 'platform-basics', label: 'Platform Basics' },
  { value: 'client-management', label: 'Client Management' },
  { value: 'loan-processing', label: 'Loan Processing' },
  { value: 'form-builder', label: 'Form Builder' },
  { value: 'rule-builder', label: 'Rule Builder' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'app-specific', label: 'App-Specific' },
  { value: 'advanced-features', label: 'Advanced Features' }
];

const DIFFICULTIES: { value: DifficultyLevel; label: string; color: string }[] = [
  { value: 'beginner', label: 'Beginner', color: '#4caf50' },
  { value: 'intermediate', label: 'Intermediate', color: '#ff9800' },
  { value: 'advanced', label: 'Advanced', color: '#f44336' }
];

const TrainingPage: React.FC = () => {
  const { user } = useAuth();
  const [guides, setGuides] = useState<TrainingGuide[]>([]);
  const [progress, setProgress] = useState<Record<string, TrainingProgress | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TrainingFilters>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const limit = 12;

  useEffect(() => {
    loadTrainingGuides();
  }, [filters, page]);

  const loadTrainingGuides = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await trainingService.getTrainingGuides(filters, page, limit);
      setGuides(response.guides);
      setTotalPages(Math.ceil(response.total / limit));

      // Load progress for each guide
      const progressPromises = response.guides.map(async (guide) => {
        try {
          const guideProgress = await trainingService.getTrainingProgress(guide.id);
          return { [guide.id]: guideProgress };
        } catch {
          return { [guide.id]: null };
        }
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap: Record<string, TrainingProgress | null> = {};
      progressResults.forEach(result => {
        Object.assign(progressMap, result);
      });
      setProgress(progressMap);
    } catch (err) {
      setError('Failed to load training guides');
      console.error('Error loading training guides:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTraining = async (guide: TrainingGuide) => {
    try {
      await trainingService.startTraining(guide.id);
      // Navigate to training guide detail view
      window.location.href = `/training/${guide.id}`;
    } catch (err) {
      console.error('Error starting training:', err);
    }
  };

  const handleContinueTraining = (guide: TrainingGuide) => {
    window.location.href = `/training/${guide.id}`;
  };

  const getProgressPercentage = (guide: TrainingGuide, guideProgress?: TrainingProgress | null) => {
    if (!guideProgress || !guide.content.sections.length) return 0;
    return (guideProgress.completedSections.length / guide.content.sections.length) * 100;
  };

  const getDifficultyColor = (difficulty: DifficultyLevel) => {
    return DIFFICULTIES.find(d => d.value === difficulty)?.color || '#757575';
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const TrainingCard: React.FC<{ guide: TrainingGuide }> = ({ guide }) => {
    const guideProgress = progress[guide.id];
    const progressPercentage = getProgressPercentage(guide, guideProgress);
    const isCompleted = guideProgress?.status === 'completed';
    const isInProgress = guideProgress?.status === 'in-progress';

    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: getDifficultyColor(guide.difficulty), mr: 2 }}>
              <SchoolIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="h3" gutterBottom>
                {guide.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Chip 
                  label={CATEGORIES.find(c => c.value === guide.category)?.label || guide.category}
                  size="small"
                  variant="outlined"
                />
                <Chip 
                  label={DIFFICULTIES.find(d => d.value === guide.difficulty)?.label || guide.difficulty}
                  size="small"
                  sx={{ color: getDifficultyColor(guide.difficulty), borderColor: getDifficultyColor(guide.difficulty) }}
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {guide.description}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDuration(guide.estimatedDuration)}
              </Typography>
            </Box>
            {isCompleted && guideProgress?.rating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {guideProgress.rating}/5
                </Typography>
              </Box>
            )}
          </Box>

          {(isInProgress || isCompleted) && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(progressPercentage)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {guide.tags && guide.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {guide.tags.slice(0, 3).map((tag, index) => (
                <Chip key={index} label={tag} size="small" variant="outlined" />
              ))}
              {guide.tags.length > 3 && (
                <Chip label={`+${guide.tags.length - 3} more`} size="small" variant="outlined" />
              )}
            </Box>
          )}
        </CardContent>

        <CardActions>
          {isCompleted ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CheckIcon />}
              onClick={() => handleContinueTraining(guide)}
            >
              Review
            </Button>
          ) : isInProgress ? (
            <Button
              fullWidth
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={() => handleContinueTraining(guide)}
            >
              Continue
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={() => handleStartTraining(guide)}
            >
              Start Training
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  if (loading && guides.length === 0) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom sx={{ mt: 3 }}>
          Training Center
        </Typography>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" height={32} sx={{ mt: 1 }} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
                </CardContent>
                <CardActions>
                  <Skeleton variant="rectangular" width="100%" height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Training Center
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enhance your skills with role-based training guides and interactive content.
            </Typography>
          </Box>
          {user && ['admin', 'lender'].includes(user.role) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => window.location.href = '/training/create'}
            >
              Create Training Guide
            </Button>
          )}
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search training guides..."
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
              value={filters.search || ''}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
            </Box>
          </Grid>
        </Grid>

        {showFilters && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category || ''}
                    label="Category"
                    onChange={(e) => setFilters({ ...filters, category: (e.target.value as TrainingCategory) || undefined })}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={filters.difficulty || ''}
                    label="Difficulty"
                    onChange={(e) => setFilters({ ...filters, difficulty: (e.target.value as DifficultyLevel) || undefined })}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {DIFFICULTIES.map((difficulty) => (
                      <MenuItem key={difficulty.value} value={difficulty.value}>
                        {difficulty.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Training Guides Grid */}
      {guides.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No training guides found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or check back later for new content.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {guides.map((guide) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={guide.id}>
                <TrainingCard guide={guide} />
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default TrainingPage;